import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { DefectRow } from "./defects/DefectRow";
import { DefectViewDialog } from "./defects/DefectViewDialog";
import { DefectFormFields } from "./defects/DefectFormFields";
import { AddListItemDialog } from "./defects/AddListItemDialog";
import { StartWalkthroughButton } from "./handover/StartWalkthroughButton";
import { ClientVisibleToggle } from "./handover/ClientVisibleToggle";
import { handoverApi, MANAGE_ROLES } from "./handover/handoverApi";
import { CloseOutDialog } from "./defects/CloseOutDialog";
import { ReopenDialog } from "./defects/ReopenDialog";

export default function DefectsList() {
  const { siteId, dwellingId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const canHandover = MANAGE_ROLES.includes(user?.role);
  const [openWalkthroughId, setOpenWalkthroughId] = useState(null);

  useEffect(() => {
    if (!canHandover) return;
    handoverApi(`/dwellings/${dwellingId}/walkthroughs`)
      .then((list) => setOpenWalkthroughId(list.find((w) => w.status === "in_progress")?.id || null))
      .catch(() => {});
  }, [dwellingId, canHandover]);
  const [dwelling, setDwelling] = useState(null);
  const [site, setSite] = useState(null);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAddAreaDialog, setShowAddAreaDialog] = useState(false);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";
  // Only an admin, or the site manager assigned to this site, can close out defects
  const canCloseOut =
    user?.role === "admin" ||
    (["site_manager", "company_owner"].includes(user?.role) &&
      Array.isArray(user?.assigned_sites) &&
      user.assigned_sites.includes(siteId));
  const [showAddElementDialog, setShowAddElementDialog] = useState(false);
  const [showAddIssueDialog, setShowAddIssueDialog] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [editDefect, setEditDefect] = useState(null);
  
  // Dropdown options
  const [areas, setAreas] = useState([]);
  const [elements, setElements] = useState([]);
  const [issues, setIssues] = useState([]);
  const [contractors, setContractors] = useState([]);
  
  // New items for "Add New" dialogs
  const [newAreaName, setNewAreaName] = useState("");
  const [newElementName, setNewElementName] = useState("");
  const [newIssueName, setNewIssueName] = useState("");
  
  // Defect form state
  const [newDefect, setNewDefect] = useState({
    area: "",
    element: "",
    issue: "",
    details: "",
    priority: "medium",
    assigned_to: "",
    due_date: null,
    images: []
  });

  useEffect(() => {
    loadSite();
    loadDwelling();
    loadDefects();
    loadDropdownOptions();
    loadContractors();
  }, [dwellingId]);

  // Auto-open the edit dialog when arriving with ?edit=<defectId> (e.g. from Reporting)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && defects.length > 0) {
      const target = defects.find((d) => d.id === editId);
      if (target) {
        handleEditDefect(target);
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [defects]);

  const loadSite = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSite(data);
      }
    } catch (error) {
      console.error("Error loading site:", error);
    }
  };

  const loadDwelling = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/dwellings/${dwellingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDwelling(data);
      }
    } catch (error) {
      console.error("Error loading dwelling:", error);
    }
  };

  const loadDefects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defects?dwelling_id=${dwellingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDefects(data);
      }
    } catch (error) {
      console.error("Error loading defects:", error);
      toast.error("Failed to load defects");
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownOptions = async () => {
    try {
      const token = localStorage.getItem("token");
      
      const [areasRes, elementsRes, issuesRes] = await Promise.all([
        fetch(`${API}/defect-areas`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/defect-elements`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/defect-issues`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (areasRes.ok) {
        const data = await areasRes.json();
        // Sort alphabetically by name
        const sortedAreas = data.sort((a, b) => a.name.localeCompare(b.name));
        setAreas(sortedAreas);
      }
      if (elementsRes.ok) {
        const data = await elementsRes.json();
        // Sort alphabetically by name
        const sortedElements = data.sort((a, b) => a.name.localeCompare(b.name));
        setElements(sortedElements);
      }
      if (issuesRes.ok) {
        const data = await issuesRes.json();
        // Sort alphabetically by name
        const sortedIssues = data.sort((a, b) => a.name.localeCompare(b.name));
        setIssues(sortedIssues);
      }
    } catch (error) {
      console.error("Error loading dropdown options:", error);
    }
  };

  const loadContractors = async () => {
    try {
      const token = localStorage.getItem("token");
      // Load all companies AND the site's defects in parallel so the list shows
      // contractors/subcontractors linked to the site OR already assigned to a
      // defect on this site (union — so the currently-assigned company is never
      // hidden, even if it isn't formally linked to the site via site_ids).
      const [companiesRes, siteDefectsRes] = await Promise.all([
        fetch(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/defects?site_id=${siteId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const data = companiesRes.ok ? await companiesRes.json() : [];
      const siteDefects = siteDefectsRes.ok ? await siteDefectsRes.json() : [];
      const assignedIds = new Set(siteDefects.map((d) => d.assigned_to).filter(Boolean));

      const contractorList = data
        .filter((c) => {
          const isType =
            c.company_type === "contractor" || c.company_type === "subcontractor";
          const linkedToSite =
            Array.isArray(c.site_ids) && c.site_ids.includes(siteId);
          return isType && (linkedToSite || assignedIds.has(c.id));
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setContractors(contractorList);
    } catch (error) {
      console.error("Error loading contractors:", error);
    }
  };

  const handleAddNewArea = async () => {
    if (!newAreaName.trim()) {
      toast.error("Please enter an area name");
      return;
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = areas.some(
      area => area.name.toLowerCase() === newAreaName.trim().toLowerCase()
    );
    
    if (isDuplicate) {
      toast.error("This area already exists");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defect-areas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newAreaName.trim() })
      });
      
      if (response.ok) {
        const newArea = await response.json();
        // Insert and sort alphabetically
        const updatedAreas = [...areas, newArea].sort((a, b) => a.name.localeCompare(b.name));
        setAreas(updatedAreas);
        setNewDefect(prev => ({ ...prev, area: newArea.name }));
        toast.success("Area added successfully");
        setNewAreaName("");
        setShowAddAreaDialog(false);
      }
    } catch (error) {
      console.error("Error adding area:", error);
      toast.error("Failed to add area");
    }
  };

  const handleAddNewElement = async () => {
    if (!newElementName.trim()) {
      toast.error("Please enter an element name");
      return;
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = elements.some(
      element => element.name.toLowerCase() === newElementName.trim().toLowerCase()
    );
    
    if (isDuplicate) {
      toast.error("This element already exists");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defect-elements`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newElementName.trim() })
      });
      
      if (response.ok) {
        const newElement = await response.json();
        // Insert and sort alphabetically
        const updatedElements = [...elements, newElement].sort((a, b) => a.name.localeCompare(b.name));
        setElements(updatedElements);
        setNewDefect(prev => ({ ...prev, element: newElement.name }));
        toast.success("Element added successfully");
        setNewElementName("");
        setShowAddElementDialog(false);
      }
    } catch (error) {
      console.error("Error adding element:", error);
      toast.error("Failed to add element");
    }
  };

  const handleAddNewIssue = async () => {
    if (!newIssueName.trim()) {
      toast.error("Please enter an issue name");
      return;
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = issues.some(
      issue => issue.name.toLowerCase() === newIssueName.trim().toLowerCase()
    );
    
    if (isDuplicate) {
      toast.error("This issue already exists");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defect-issues`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newIssueName.trim() })
      });
      
      if (response.ok) {
        const newIssue = await response.json();
        // Insert and sort alphabetically
        const updatedIssues = [...issues, newIssue].sort((a, b) => a.name.localeCompare(b.name));
        setIssues(updatedIssues);
        setNewDefect(prev => ({ ...prev, issue: newIssue.name }));
        toast.success("Issue added successfully");
        setNewIssueName("");
        setShowAddIssueDialog(false);
      }
    } catch (error) {
      console.error("Error adding issue:", error);
      toast.error("Failed to add issue");
    }
  };

  const compressImage = (file, maxWidth = 800) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          resolve(compressedBase64);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (event, isEdit = false) => {
    const files = Array.from(event.target.files);
    const currentImages = isEdit ? editDefect.images : newDefect.images;
    
    if (currentImages.length + files.length > 3) {
      toast.error("Maximum 3 images allowed per defect");
      return;
    }

    try {
      const compressedImages = await Promise.all(
        files.map(file => compressImage(file))
      );

      if (isEdit) {
        setEditDefect(prev => ({
          ...prev,
          images: [...prev.images, ...compressedImages]
        }));
      } else {
        setNewDefect(prev => ({
          ...prev,
          images: [...prev.images, ...compressedImages]
        }));
      }
    } catch (error) {
      console.error("Error compressing images:", error);
      toast.error("Failed to process images");
    }
  };

  const removeImage = (index, isEdit = false) => {
    if (isEdit) {
      setEditDefect(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } else {
      setNewDefect(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAddDefect = async () => {
    if (!newDefect.area || !newDefect.element || !newDefect.issue) {
      toast.error("Please fill in Area, Element, and Issue fields");
      return;
    }

    if (!dwelling) {
      toast.error("Dwelling information not loaded");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          site_id: dwelling.site_id,
          dwelling_id: dwelling.id,
          dwelling_number: dwelling.dwelling_number,
          area: newDefect.area,
          element: newDefect.element,
          issue: newDefect.issue,
          details: newDefect.details,
          priority: newDefect.priority,
          assigned_to: newDefect.assigned_to,
          due_date: newDefect.due_date ? format(newDefect.due_date, 'yyyy-MM-dd') : null,
          images: newDefect.images
        })
      });

      if (response.ok) {
        toast.success("Defect added successfully!");
        setShowAddDialog(false);
        setNewDefect({
          area: "",
          element: "",
          issue: "",
          details: "",
          priority: "medium",
          assigned_to: "",
          due_date: null,
          images: []
        });
        loadDefects();
        loadDwelling(); // Reload dwelling to update counts
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to add defect");
      }
    } catch (error) {
      console.error("Error adding defect:", error);
      toast.error("Failed to add defect");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (defectId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defects/${defectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success("Status updated successfully!");
        loadDefects();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const [closeOutDefect, setCloseOutDefect] = useState(null);
  const [reopenDefect, setReopenDefect] = useState(null);
  // Accepts a defect or its id (the view dialog passes the id)
  const handleCloseOut = (defectOrId) => {
    const d = typeof defectOrId === "string" ? defects.find((x) => x.id === defectOrId) : defectOrId;
    setShowViewDialog(false);
    setCloseOutDefect(d || null);
  };

  const handleEditDefect = (defect) => {
    setEditDefect({
      ...defect,
      due_date: defect.due_date ? new Date(defect.due_date) : null,
      images: defect.images || []
    });
    setShowEditDialog(true);
  };

  const handleUpdateDefect = async () => {
    if (!editDefect.area || !editDefect.element || !editDefect.issue) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const updateData = {
        ...editDefect,
        due_date: editDefect.due_date ? format(editDefect.due_date, "yyyy-MM-dd") : null
      };

      const response = await fetch(`${API}/defects/${editDefect.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success("Defect updated successfully");
        setShowEditDialog(false);
        setEditDefect(null);
        loadDefects();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update defect");
      }
    } catch (error) {
      console.error("Error updating defect:", error);
      toast.error("Failed to update defect");
    }
  };

  const handleDeleteDefect = async (defectId) => {
    if (!window.confirm("Are you sure you want to delete this defect?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/defects/${defectId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Defect deleted successfully");
        loadDefects();
      }
    } catch (error) {
      console.error("Error deleting defect:", error);
      toast.error("Failed to delete defect");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-red-100 text-red-700";
      case "in_progress":
        return "bg-amber-100 text-amber-700";
      case "completed":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700";
      case "medium":
        return "bg-amber-100 text-amber-700";
      case "low":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate(`/defects/${siteId}/details`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="heading-primary text-2xl sm:text-3xl">
              {site && dwelling ? `${site.name} - ${dwelling.dwelling_number}` : "Loading..."}
            </h1>
            <p className="text-slate-600">Manage defects for this dwelling</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canHandover && (
            <StartWalkthroughButton dwellingId={dwellingId} openWalkthroughId={openWalkthroughId} testId="dwelling-start-walkthrough-button" />
          )}
          {!isViewOnly && (
            <Button onClick={() => setShowAddDialog(true)} className="btn-construction">
              <Plus className="w-4 h-4 mr-2" />
              Add Defect
            </Button>
          )}
        </div>
      </div>

      {/* Defects List */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle>Defects</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
            </div>
          ) : defects.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>No defects found</p>
              <p className="text-sm">Click "Add Defect" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Number</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Area</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Element</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Issue</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Priority</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Assigned To</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Due Date</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {defects.map((defect) => (
                    <DefectRow
                      key={defect.id}
                      defect={defect}
                      contractors={contractors}
                      isViewOnly={isViewOnly}
                      canCloseOut={canCloseOut}
                      getPriorityColor={getPriorityColor}
                      onView={(d) => {
                        setSelectedDefect(d);
                        setShowViewDialog(true);
                      }}
                      onEdit={handleEditDefect}
                      onDelete={handleDeleteDefect}
                      onCloseOut={handleCloseOut}
                      onReopen={setReopenDefect}
                      onStatusChange={handleUpdateStatus}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Defect Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Defect</DialogTitle>
            <DialogDescription>
              Fill in the details below to add a new defect to this dwelling.
            </DialogDescription>
          </DialogHeader>
          <DefectFormFields
            value={newDefect}
            setValue={setNewDefect}
            areas={areas}
            elements={elements}
            issues={issues}
            contractors={contractors}
            showAddButtons={true}
            onAddArea={() => setShowAddAreaDialog(true)}
            onAddElement={() => setShowAddElementDialog(true)}
            onAddIssue={() => setShowAddIssueDialog(true)}
            unassignedValue=""
            imageInputId="defect-image-upload"
            onImageUpload={handleImageUpload}
            onRemoveImage={removeImage}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDefect} disabled={loading}>
              {loading ? "Adding..." : "Add Defect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Defect Dialog */}
      {editDefect && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Defect - {editDefect.number}</DialogTitle>
              <DialogDescription>
                Update the details of this defect.
              </DialogDescription>
            </DialogHeader>
            <DefectFormFields
              value={editDefect}
              setValue={setEditDefect}
              areas={areas}
              elements={elements}
              issues={issues}
              contractors={contractors}
              showStatus={true}
              unassignedValue={null}
              imageInputId="edit-defect-image-upload"
              onImageUpload={(e) => handleImageUpload(e, true)}
              onRemoveImage={(index) => removeImage(index, true)}
            />
            {canHandover && (
              <ClientVisibleToggle defect={editDefect} onChanged={(patch) => {
                setEditDefect((p) => ({ ...p, ...patch }));
                loadDefects();
              }} />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowEditDialog(false);
                setEditDefect(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateDefect} disabled={loading}>
                {loading ? "Updating..." : "Update Defect"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* View Defect Dialog */}
      <DefectViewDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        defect={selectedDefect}
        contractors={contractors}
        isViewOnly={isViewOnly}
        canCloseOut={canCloseOut}
        getStatusColor={getStatusColor}
        getPriorityColor={getPriorityColor}
        onEdit={handleEditDefect}
        onCloseOut={handleCloseOut}
        onDelete={handleDeleteDefect}
      />
      <CloseOutDialog defect={closeOutDefect} onOpenChange={(o) => !o && setCloseOutDefect(null)} onDone={loadDefects} />
      <ReopenDialog defect={reopenDefect} onOpenChange={(o) => !o && setReopenDefect(null)} onDone={loadDefects} />

      {/* Add New Area / Element / Issue Dialogs */}
      <AddListItemDialog
        open={showAddAreaDialog}
        onOpenChange={setShowAddAreaDialog}
        title="Add New Area"
        description="Create a new defect area category."
        label="Area Name *"
        placeholder="e.g., Garage, Patio"
        value={newAreaName}
        onChange={setNewAreaName}
        onSubmit={handleAddNewArea}
        onCancel={() => {
          setShowAddAreaDialog(false);
          setNewAreaName("");
        }}
        submitLabel="Add Area"
      />

      <AddListItemDialog
        open={showAddElementDialog}
        onOpenChange={setShowAddElementDialog}
        title="Add New Element"
        description="Create a new defect element category."
        label="Element Name *"
        placeholder="e.g., Molding, Window Frame"
        value={newElementName}
        onChange={setNewElementName}
        onSubmit={handleAddNewElement}
        onCancel={() => {
          setShowAddElementDialog(false);
          setNewElementName("");
        }}
        submitLabel="Add Element"
      />

      <AddListItemDialog
        open={showAddIssueDialog}
        onOpenChange={setShowAddIssueDialog}
        title="Add New Issue"
        description="Create a new defect issue category."
        label="Issue Name *"
        placeholder="e.g., Out of Alignment, Discolored"
        value={newIssueName}
        onChange={setNewIssueName}
        onSubmit={handleAddNewIssue}
        onCancel={() => {
          setShowAddIssueDialog(false);
          setNewIssueName("");
        }}
        submitLabel="Add Issue"
      />
    </div>
  );
}
